/**
 * Hook for managing user CRUD operations and state
 *
 * Refactored to use extracted hooks:
 * - useUserForm: Form state and validation
 * - useUserFilters: Search and filter state
 * - useUserCrud: Create/edit/delete operations and the pending-edits buffer
 *
 * This hook now focuses on:
 * - Loading users from API
 * - Coordinating between form, filter, and CRUD hooks
 */
import { useState, useEffect, useCallback } from 'react';
import * as Auth from 'aws-amplify/auth';

import Util from '../util/util.js';
import { useErrorState, showSuccess } from '../util/error';
import {
  USER_MODES,
  VALID_ROLES,
  processUserData,
} from '../util/user';

import useUserForm from './useUserForm';
import useUserFilters from './useUserFilters';
import useUserCrud from './useUserCrud';
import { t } from '../i18n';

// Re-export for backward compatibility
export { USER_MODES };

export default function useUserManagement(organization) {
  // Error state
  const { error, hasError, handleError, clearError } = useErrorState({
    context: 'UserManagement',
    showToasts: true,
  });

  // User data state
  const [existingUsers, setExistingUsers] = useState({});
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Bulk upload state
  const [uploadErrors, setUploadErrors] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);

  // Use extracted form hook
  const formHook = useUserForm({
    existingUsers,
    authenticatedUser,
    usersLoaded,
  });

  // Use extracted filters hook
  const filtersHook = useUserFilters(organization, existingUsers, formHook.mode);

  // Use extracted CRUD hook
  const crudHook = useUserCrud(organization, {
    formHook,
    filtersHook,
    setExistingUsers,
    handleError,
  });

  /**
   * Get all users for the organization
   */
  const getUsers = useCallback(async () => {
    try {
      let organizations = filtersHook.validOrgs.length > 1 ? filtersHook.validOrgs : [organization];
      let cleanedUserData = {};

      for (let org of organizations) {
        const result = await Util.get('UserManagement', '/users', {
          queryParams: { organization: org },
        });

        const orgUserData = processUserData(result.data.Users, result.data.roles, org);
        cleanedUserData = { ...cleanedUserData, ...orgUserData };
      }

      setExistingUsers(cleanedUserData);
      if (!Object.keys(filtersHook.filteredUsers).length) {
        filtersHook.setFilteredUsers(cleanedUserData);
      }

      showSuccess(t('admin:users.loaded'));
      setUsersLoaded(true);
    } catch (err) {
      handleError(err, { userMessage: t('admin:users.loadFailed') });
    }
    formHook.setUpdatingUser(false);
  }, [filtersHook.validOrgs, organization, handleError, formHook]);

  /**
   * Extended switch mode that coordinates with filters
   */
  const switchMode = useCallback((newMode) => {
    if (!filtersHook.OrgFilterValue.length) {
      filtersHook.setOrgFilterValue([organization]);
    }

    formHook.switchMode(newMode);
    setUploadErrors(null);
    // Filtering happens automatically via useEffect when mode changes
  }, [filtersHook, organization, formHook]);

  /**
   * Extended enter email that also updates search
   */
  const enterEmail = useCallback((text) => {
    formHook.enterEmail(text);
    filtersHook.setSearchText(text);
  }, [formHook, filtersHook]);

  // Initialize on mount
  useEffect(() => {
    filtersHook.updateValidOrgs().then(() => {
      Auth.fetchUserAttributes().then((user) => {
        setAuthenticatedUser(user);
      });
    });
  }, []);

  // Load users when orgs are ready
  useEffect(() => {
    if (filtersHook.validOrgs.length > 0 && !usersLoaded) {
      getUsers();
    }
  }, [filtersHook.validOrgs]);

  return {
    // Error state
    error,
    hasError,
    clearError,

    // User data
    existingUsers,
    filteredUsers: filtersHook.filteredUsers,
    editUsers: crudHook.editUsers,
    authenticatedUser,
    usersLoaded,

    // Mode and UI (from form hook)
    mode: formHook.mode,
    showUsers: filtersHook.showUsers,
    updatingUser: formHook.updatingUser,

    // Form state (from form hook)
    username: formHook.username,
    password: formHook.password,
    setPassword: formHook.setPassword,
    email: formHook.email,
    errors: formHook.errors,
    isFormValid: formHook.isFormValid,
    displayErrors: formHook.displayErrors,
    submitEnabled: formHook.submitEnabled,

    // Search/filter (from filters hook)
    searchText: filtersHook.searchText,
    setSearchText: filtersHook.setSearchText,
    roleSearchOpen: filtersHook.roleSearchOpen,
    setRoleSearchOpen: filtersHook.setRoleSearchOpen,
    roleSearchValue: filtersHook.roleSearchValue,
    setRoleSearchValue: filtersHook.setRoleSearchValue,
    roleSearchFilter: filtersHook.roleSearchFilter,
    setRoleSearchFilter: filtersHook.setRoleSearchFilter,
    statusSearchOpen: filtersHook.statusSearchOpen,
    setStatusSearchOpen: filtersHook.setStatusSearchOpen,
    statusSearchValue: filtersHook.statusSearchValue,
    setStatusSearchValue: filtersHook.setStatusSearchValue,
    statusSearchFilter: filtersHook.statusSearchFilter,
    setStatusSearchFilter: filtersHook.setStatusSearchFilter,
    tierSearchOpen: filtersHook.tierSearchOpen,
    setTierSearchOpen: filtersHook.setTierSearchOpen,
    tierSearchValue: filtersHook.tierSearchValue,
    setTierSearchValue: filtersHook.setTierSearchValue,
    tierSearchFilter: filtersHook.tierSearchFilter,
    setTierSearchFilter: filtersHook.setTierSearchFilter,

    // Role dropdown (from form hook)
    rolesOpen: formHook.rolesOpen,
    setRolesOpen: formHook.setRolesOpen,
    rolesFilterValue: formHook.rolesFilterValue,
    setRolesFilterValue: formHook.setRolesFilterValue,
    rolesFilter: formHook.rolesFilter,
    setRolesFilter: formHook.setRolesFilter,

    // Organization filters (from filters hook)
    validOrgs: filtersHook.validOrgs,
    loadingOrg: filtersHook.loadingOrg,
    OrgFilterOpen: filtersHook.OrgFilterOpen,
    setOrgFilterOpen: filtersHook.setOrgFilterOpen,
    OrgFilterValue: filtersHook.OrgFilterValue,
    setOrgFilterValue: filtersHook.setOrgFilterValue,
    OrgFilter: filtersHook.OrgFilter,
    setOrgFilter: filtersHook.setOrgFilter,
    OrgFilterOpen2: filtersHook.OrgFilterOpen2,
    setOrgFilterOpen2: filtersHook.setOrgFilterOpen2,
    OrgFilterValue2: filtersHook.OrgFilterValue2,
    setOrgFilterValue2: filtersHook.setOrgFilterValue2,
    OrgFilter2: filtersHook.OrgFilter2,
    setOrgFilter2: filtersHook.setOrgFilter2,
    OrgFilterOpen3: filtersHook.OrgFilterOpen3,
    setOrgFilterOpen3: filtersHook.setOrgFilterOpen3,
    OrgFilterValue3: filtersHook.OrgFilterValue3,
    setOrgFilterValue3: filtersHook.setOrgFilterValue3,
    OrgFilter3: filtersHook.OrgFilter3,
    setOrgFilter3: filtersHook.setOrgFilter3,

    // Bulk upload
    uploadErrors,
    setUploadErrors,
    fileLoading,
    setFileLoading,

    // Actions
    switchMode,
    clearInput: formHook.clearInput,
    enterEmail,
    createUser: crudHook.createUser,
    editUser: crudHook.editUser,
    deleteUser: crudHook.deleteUser,
    postCreateUser: crudHook.postCreateUser,
    savePasswordReset: crudHook.savePasswordReset,
    saveRoleUpdate: crudHook.saveRoleUpdate,
    saveOrgUpdate: crudHook.saveOrgUpdate,
    saveOrgAccessUpdate: crudHook.saveOrgAccessUpdate,
    filterUsers: filtersHook.filterUsers,
    setDisplayErrors: formHook.setDisplayErrors,

    // Constants
    VALID_ROLES,
  };
}
